import auth from '@react-native-firebase/auth';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';
import {useQuery} from 'react-query';

export interface PersonHolder {
  personIds?: FirebaseFirestoreTypes.DocumentReference<Person>[];
  people?: Person[];
  personId?: FirebaseFirestoreTypes.DocumentReference<Person>;
  person?: Person;
}

export type PersonInfo = {
  person: Person;
  isAdmin: boolean;
  isGuestRelations: boolean;
  isMember: boolean;
};

export enum PersonRoles {
  Person = 'person',
  Member = 'user',
  Admin = 'admin',
  GuestRelations = 'guestRelations',
}

export type Person = {
  authUid: string;
  personId: number;
  firstName: string;
  lastName: string;
  email: string;
  emailSecondary?: string;
  phoneNumber1?: string;
  phoneNumber2?: string;
  hubspotId?: number;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  roles?: PersonRoles[];
  contactNote?: string;
  ref: FirebaseFirestoreTypes.DocumentReference<Person>;
};

export async function fetchAuthPersonInfo(): Promise<PersonInfo | undefined> {
  const user = auth().currentUser;
  if (user === undefined || user === null) {
    console.log('Refusing to search for undefined current user');
    return;
  }
  let personSnapshot = await firestore()
    .collection('people')
    .where('authUid', '==', user.uid)
    // .orderBy('firstName')
    .get();

  // If there is more than one person for one auth UID, it is unrecoverable.
  if (personSnapshot.size > 1) {
    console.warn(
      'More than one firestore person for firebase auth userid ' + user.uid,
    );
    return undefined;
  }
  // console.log('person length is: ' + JSON.stringify(personSnapshot.size));
  if (personSnapshot.size < 1) {
    console.warn(
      `Authenticated ${user.email?.toLowerCase()}, but no firestore user. Deleted? changed email?`,
    );

    // see if the auth email exists, and if so update the auth id since that
    // means that an admin set the person's email to something but the auth id is stale
    personSnapshot = await firestore()
      .collection('people')
      .where('email', '==', user.email?.toLowerCase())
      .get();

    // If there is more than one person for one email, it is unrecoverable.
    if (personSnapshot.size > 1) {
      console.warn(
        'More than one firestore person for firebase auth userid ' + user.uid,
      );
      return undefined;
    }
    // console.log('person length is: ' + JSON.stringify(personSnapshot.size));
    if (personSnapshot.size < 1) {
      console.warn(
        `Authenticated, but no firestore user for ${user.email?.toLowerCase()}. Deleted?`,
      );
      auth().signOut();
      return undefined;
    }

    // Okay, we have one user for the email but not the auth id. Update auth id.
    console.log('Authenticated, new email / stale authid. Updating');
    await firestore()
      .doc(`people/${personSnapshot.docs[0].id}`)
      .set({authUid: user.uid}, {merge: true});
  }
  // console.log('person is: ' + JSON.stringify(personSnapshot.docs[0].data()));

  const person = personSnapshot.docs[0].data() as Person;
  person.authUid = user.uid;

  return {
    person,
    isMember: person.roles?.includes(PersonRoles.Member) ?? false,
    isAdmin: person.roles?.includes(PersonRoles.Admin) ?? false,
    isGuestRelations:
      person.roles?.includes(PersonRoles.GuestRelations) ?? false,
  };
}

export function useAuthPersonInfo(): PersonInfo | undefined {
  const personQueryResult = useQuery(
    ['authPersonInfo', auth().currentUser],
    fetchAuthPersonInfo,
  );
  if (personQueryResult.isLoading) {
    return;
  }
  if (personQueryResult.isError) {
    console.warn('we got an error? ' + personQueryResult.error);
    return;
  }
  return personQueryResult.data;
}

async function fetchPeople(
  roles: string[] = ['user'],
): Promise<Person[] | undefined> {
  const snapshot = await firestore()
    .collection('people')
    .where('roles', 'array-contains-any', roles)
    .orderBy('firstName')
    .get();
  return snapshot.docs.map(doc => {
    const data = doc.data() as Person;
    data.ref = doc.ref as FirebaseFirestoreTypes.DocumentReference<Person>;
    return data as Person;
  });
}

export function usePeople(roles: string[] = ['user']): Person[] | undefined {
  const queryResult = useQuery(['people', roles], () => fetchPeople(roles));
  if (
    queryResult.isLoading ||
    queryResult.isError ||
    queryResult.data === undefined
  ) {
    return;
  }
  return queryResult.data;
}

export function injectPeople(
  holders: PersonHolder[],
  people: ReadonlyArray<Person>,
) {
  holders.forEach(holder => {
    if (holder.personId !== undefined) {
      holder.person = people.find(person =>
        person.ref?.path === holder.personId?.path ? true : false,
      );
    }
    if (holder.personIds !== undefined) {
      holder.people = [];
    }
    holder.personIds?.forEach(personId => {
      if (holder.people === undefined) {
        holder.people = [];
      }
      const match = people.find(person =>
        person.ref?.path === personId.path ? true : false,
      );
      if (match !== undefined) {
        holder.people.push(match);
      }
    });
  });
}

export const displayNameFromPerson = (person?: Person): string => {
  let displayName = person?.email ?? '';
  if (person?.firstName) {
    displayName =
      person.firstName + (person.lastName ? ' ' + person.lastName : '');
  }
  return displayName;
};
